import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faTools, 
  faQuestionCircle, 
  faEnvelope, 
  faPhone, 
  faCheck, 
  faBuilding, 
  faHammer, 
  faWrench,
  faBars,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import Logo from './Logo';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Détermine si on a scrollé vers le bas ou vers le haut
      if (currentScrollY > lastScrollY) {
        // Scrolling vers le bas - cacher la navbar
        setVisible(false);
      } else {
        // Scrolling vers le haut - montrer la navbar
        setVisible(true);
      }
      
      // Met à jour l'état scrolled pour le style de la navbar
      if (currentScrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
      
      // Enregistre la position de défilement actuelle
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    // Si on ouvre le menu, empêcher le défilement du body
    if (!isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };
  
  // S'assurer que le défilement du body est rétabli quand le composant est démonté
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const navLinks = [
    { name: 'Accueil', path: '/', icon: faHome, description: '' },
    { name: 'Services', path: '/services', icon: faTools, description: '' },
    { name: 'FAQ', path: '/faq', icon: faQuestionCircle, description: '' },
    { name: 'Contact', path: '/contact', icon: faEnvelope, description: '' },
  ];

  const serviceLinks = [
    { name: 'Travaux neuves', path: '/services', icon: faBuilding, description: 'Réalisation de projets résidentiels et commerciaux.' },
    { name: 'Rénovation', path: '/services', icon: faHammer, description: 'Transformation et modernisation de vos espaces.' },
    { name: 'Réparation et maintenance', path: '/services', icon: faWrench, description: 'Entretien et réparation de vos installations.' }
  ];

  // Classes pour la barre de navigation - toujours fond blanc
  const navbarClasses = `fixed w-full z-50 transition-all duration-300 bg-white shadow-md py-4
    ${visible ? 'translate-y-0' : '-translate-y-full'}`;

  // Classes pour les liens de navigation - toujours texte foncé sur fond blanc
  const getLinkClasses = (path: string) => {
    const isActive = location.pathname === path;
    return `font-heading font-medium text-base tracking-wide transition-colors ${
      isActive ? 'text-primary font-semibold' : 'text-gray-800 hover:text-primary'
    }`;
  };

  // Classe pour l'icône du menu mobile - toujours gris foncé
  const menuIconClass = "text-xl text-gray-800";

  return (
    <nav className={navbarClasses}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center">
          <Logo className="z-20" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={getLinkClasses(link.path)}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Bouton de menu hamburger mobile */}
          <div className="flex items-center md:hidden">
            <button
              onClick={toggleMenu}
              className="p-2 bg-primary text-white rounded-md focus:outline-none"
              aria-label="Menu"
            >
              <FontAwesomeIcon icon={isOpen ? faTimes : faBars} className="text-xl" />
              <span className="ml-2">Menu</span>
            </button>
          </div>
        </div>

        {/* Menu mobile - utilise position absolute pour éviter les problèmes de z-index */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg md:hidden overflow-auto max-h-[85vh] border-t border-gray-200">
            {/* En-tête du menu */}
            <div className="p-4 flex justify-between items-center border-b border-gray-200 bg-white">
              <h2 className="font-bold text-xl text-gray-800">Menu</h2>
              <button
                onClick={() => {
                  setIsOpen(false);
                  document.body.style.overflow = '';
                }}
                className="p-2 bg-gray-100 rounded-full focus:outline-none"
                aria-label="Fermer le menu"
              >
                <FontAwesomeIcon icon={faTimes} className="text-gray-800" />
              </button>
            </div>
            
            {/* Liens de navigation principaux */}
            <div className="p-4 pt-6 text-black">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`flex items-center py-3 px-4 mb-2 rounded-lg transition-colors ${location.pathname === link.path ? 'text-primary font-bold bg-gray-50' : 'text-gray-800 hover:bg-gray-50'}`}
                  onClick={() => setIsOpen(false)}
                >
                  <div className="mr-4 w-8 text-primary">
                    <FontAwesomeIcon icon={link.icon} className="text-xl" />
                  </div>
                  <span className="font-medium">{link.name}</span>
                  {location.pathname === link.path && (
                    <div className="ml-auto">
                      <FontAwesomeIcon icon={faCheck} className="text-primary" />
                    </div>
                  )}
                </Link>
              ))}
            </div>

            {/* Services section - style similaire à l'image */}
            <div className="p-4 pt-2 border-t border-gray-100">
              <h3 className="text-xl font-bold mb-4 text-gray-800 px-4">Nos Services</h3>
              <div className="grid grid-cols-1 gap-4">
                {serviceLinks.map((service) => (
                  <Link
                    key={service.name}
                    to={service.path}
                    className="flex items-start p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                    onClick={() => {
                      setIsOpen(false);
                      document.body.style.overflow = '';
                    }}
                  >
                    <div className="mr-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 text-primary">
                      <FontAwesomeIcon icon={service.icon} className="text-xl" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">{service.name}</h4>
                      <p className="text-sm text-gray-500">{service.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Coordonnées en bas - ajusté pour être plus visible sur mobile */}
            <div className="p-6 pb-20 mt-4 bg-gray-50 border-t border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Contactez-nous</h3>
              <div className="space-y-4">
                <a href="mailto:mgecouverture@gmail.com" className="flex items-center text-primary text-lg">
                  <FontAwesomeIcon icon={faEnvelope} className="mr-3 text-xl" />
                  <span>mgecouverture@gmail.com</span>
                </a>
                <p className="text-base text-gray-600 mt-2">34 Rue de la Foret, 91860 Epinay-Sous-Senart</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
